/**
 * Course HTML renderer that accepts raw HTML, encoded entities, or markdown-in-paragraphs.
 */
import React, { memo, useMemo } from "react";
import { marked } from "marked";

import { sanitizeHtml } from "./SafeHtml";

/**
 * Renders course HTML, including entity-encoded markup and markdown wrapped in paragraphs.
 *
 * @param {{ html?: string, className?: string }} props
 */
const HtmlRenderer = ({ html = "", className = "" }) => {
  /**
   * Decodes HTML entities with a textarea.
   *
   * @param {string} input Encoded string.
   * @returns {string}
   */
  const decodeHtmlEntities = (input = "") => {
    // Browser-only decode (safe and fast)
    const txt = document.createElement("textarea");
    txt.innerHTML = input;
    return txt.value;
  };

  // Converts: <p>## Title</p><p></p><p>* item</p> -> "## Title\n\n* item"
  /**
   * Unwraps `<p>`-wrapped markdown into a plain markdown string.
   *
   * @param {string} rawHtml HTML fragment.
   * @returns {string}
   */
  const unwrapParagraphMarkdown = (rawHtml = "") => {
    let s = rawHtml;

    // Join paragraphs with newlines
    s = s.replace(/<\/p>\s*<p>/gi, "\n");

    // Remove <p> wrappers
    s = s.replace(/<\/?p>/gi, "");

    // Convert <br> to newline
    s = s.replace(/<br\s*\/?>/gi, "\n");

    // Decode any entities (&nbsp;, etc.)
    s = decodeHtmlEntities(s);

    return s.trim();
  };

  const looksLikeMarkdown = (text = "") => {
    const s = text.trim();
    // Strong markdown signals (headings, lists, bold, inline code)
    return (
      /^#{1,6}\s+/m.test(s) || // # Heading
      /^\s*[-*+]\s+/m.test(s) || // - item / * item
      /^\s*\d+\.\s+/m.test(s) || // 1. item
      /\*\*[^*]+\*\*/.test(s) || // **bold**
      /`{1,3}[^`]+`{1,3}/.test(s) // `code` or ```code```
    );
  };

  const finalHtml = useMemo(() => {
    if (!html) return "";

    // Decode entities first (handles the "&lt;h2&gt;" format)
    const decoded = decodeHtmlEntities(html);

    // Try markdown conversion only if it clearly looks like markdown
    const maybeMd = unwrapParagraphMarkdown(decoded);
    if (looksLikeMarkdown(maybeMd)) {
      return sanitizeHtml(marked.parse(maybeMd, { breaks: true }), { course: true });
    }

    // Otherwise treat as regular HTML
    return sanitizeHtml(decoded, { course: true });
  }, [html]);

  return <div className={className} dangerouslySetInnerHTML={{ __html: finalHtml }} />;
};

export default memo(HtmlRenderer);
